import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { MsalService } from '@azure/msal-angular';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { DatabaseCacheService, Section } from '@app/data/cache/database-cache.service';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { SUPPORT_I18N } from './support.i18n';

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  styleUrls: ['./support.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: SHARED_IMPORTS,
})
export class SupportComponent implements OnInit {
  private readonly msalService = inject(MsalService);
  private readonly storeAuth = inject(StoreAuthService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dbCache = inject(DatabaseCacheService);
  private readonly fb = inject(FormBuilder);

  protected readonly i18n = SUPPORT_I18N;
  protected readonly isSubmitting = signal(false);
  protected readonly isIdentityLoading = signal(true);
  protected readonly isEmailEditing = signal(false);
  protected readonly submissionCompleted = signal(false);
  protected readonly submittedEmail = signal('');
  protected readonly identity = signal<SupportIdentity | null>(null);
  protected readonly selectedSection = signal<Section | null>(null);

  protected supportForm!: FormGroup;

  ngOnInit(): void {
    this.supportForm = this.fb.group({
      reason: ['', [Validators.required]],
      sectionName: [''],
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      changeEmail: [false],
      subject: [''],
      description: ['', [Validators.required]],
    });

    this.selectedSection.set(this.dbCache.getSelectedSection() ?? null);
    void this.loadIdentity();
  }

  protected reviewForm(stepper: MatStepper): void {
    if (this.supportForm.invalid || this.isIdentityLoading() || !this.selectedSection()) {
      this.supportForm.markAllAsTouched();
      return;
    }

    stepper.next();
  }

  protected async submitForm(stepper: MatStepper): Promise<void> {
    const selectedSection = this.selectedSection();
    if (this.supportForm.invalid || this.isSubmitting() || this.isIdentityLoading() || !selectedSection) {
      this.supportForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    const formValue = this.supportForm.getRawValue();

    const body = new HttpParams()
      .set('orgid', '00D2w000004sP2x')
      .set('retURL', '"http://"')
      .set('debug', '0')
      .set('debugEmail', '')
      .set('reason', formValue.reason)
      .set('name', formValue.name)
      .set('company', selectedSection.sectionName)
      .set('00NAy000000PTtA', selectedSection.sectionId)
      .set('email', formValue.email)
      .set('subject', formValue.subject)
      .set('description', formValue.description);

    try {
      await fetch(
        'https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8',
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: body.toString(),
        },
      );
      this.submittedEmail.set(formValue.email);
      this.submissionCompleted.set(true);
      this.supportForm.markAsPristine();
      const confirmationStep = stepper.steps.get(1);
      if (confirmationStep) {
        confirmationStep.completed = true;
      }
      stepper.next();
    } catch {
      this.snackBar.open(this.i18n.snackBar.error, this.i18n.snackBar.close, { duration: 3000 });
    } finally {
      this.isSubmitting.set(false);
    }
  }

  protected clearForm(): void {
    this.resetForm();
  }

  protected returnToSupportTop(stepper: MatStepper): void {
    stepper.reset();
    this.resetForm();
  }

  protected toggleEmailEditing(shouldEdit: boolean): void {
    this.isEmailEditing.set(shouldEdit);
    if (!shouldEdit) {
      const emailControl = this.supportForm.get('email');
      emailControl?.setValue(this.identity()?.email ?? '');
      emailControl?.markAsPristine();
      emailControl?.markAsUntouched();
    }
  }

  private resetForm(): void {
    this.isSubmitting.set(false);
    this.isEmailEditing.set(false);
    this.submissionCompleted.set(false);
    this.submittedEmail.set('');
    const identity = this.identity();
    this.supportForm.reset({
      reason: '',
      sectionName: identity?.sectionName ?? '',
      name: identity?.name ?? '',
      email: identity?.email ?? '',
      changeEmail: false,
      subject: '',
      description: '',
    });
  }

  private async loadIdentity(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const userIdFromToken = this.readString(claims?.['sub']);

    let response: LoginUserResponse | null = null;
    try {
      response = await this.dbCache.setLoginUserAndSelectedSection(
        this.selectedSection()?.sectionId,
      ) as LoginUserResponse;
    } catch {
      // The authenticated token and existing section cache still provide a
      // usable fallback when refreshing the login-user cache fails.
    }

    const userDocument = response?.userDocument;
    const selectedSection = this.dbCache.getSelectedSection() ?? this.selectedSection();
    this.selectedSection.set(selectedSection ?? null);

    const name = [userDocument?.lastName, userDocument?.firstName]
      .filter((part): part is string => Boolean(part?.trim()))
      .join(' ')
      || this.storeAuth.getLoginUserName()
      || account?.name
      || '';
    const email = this.readEmail(userDocument?.contactDetails?.email)
      || this.readEmailClaim(claims)
      || this.readEmail(account?.username);

    const identity: SupportIdentity = {
      userId: userDocument?.userId || userIdFromToken,
      name,
      email,
      sectionId: selectedSection?.sectionId ?? '',
      sectionName: selectedSection?.sectionName ?? '',
    };

    this.identity.set(identity);
    this.isEmailEditing.set(false);
    this.supportForm.patchValue({
      sectionName: identity.sectionName,
      name: identity.name,
      email: identity.email,
      changeEmail: false,
    });
    this.isIdentityLoading.set(false);
  }

  private readEmailClaim(claims: Record<string, unknown> | undefined): string {
    const emails = claims?.['emails'];
    if (Array.isArray(emails)) {
      const email = emails
        .map((value) => this.readEmail(value))
        .find((value) => value.length > 0);
      if (email) return email;
    }
    return this.readEmail(claims?.['email']);
  }

  private readEmail(value: unknown): string {
    const email = this.readString(value);
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}

interface SupportIdentity {
  userId: string;
  name: string;
  email: string;
  sectionId: string;
  sectionName: string;
}

interface LoginUserResponse {
  userDocument?: {
    userId?: string;
    lastName?: string;
    firstName?: string;
    contactDetails?: {
      email?: string;
    };
  };
}
